import classes from './Details.module.sass'
import { CategoryInterface } from '../../../../../common/types/interfaces'
import { useSelector } from 'react-redux'
import { selectClusterImageUrl } from '../../../../../store/slices/currentClusterSlice'
import {
  fieldTypeToText,
  valueTypeToText,
  unitTypeToShort,
} from '../../../../../common/utils/functions/enumToText'
import { SystemType, Unit } from '../../../../../common/types/enums'
import { format } from 'date-fns'

type Props = {
  el?: CategoryInterface
}

export const Details = ({ el }: Props) => {
  const imageUrl = useSelector(selectClusterImageUrl)

  if (!el) return null

  const formatDate = (date?: string) => {
    if (!date) return '—'
    return format(new Date(date), 'dd.MM.yyyy HH:mm')
  }

  return (
    <div className={classes.container}>
      <div className={classes.title}>детали категории</div>
      <div className={classes.name}>{el.name}</div>
      <div className={classes.description}>{el.description || 'Нет описания'}</div>

      {/* ============================================================ */}
      {/* СИСТЕМНАЯ ИНФОРМАЦИЯ */}
      {/* ============================================================ */}
      <div className={classes.sectionTitle}>Системная информация</div>
      <div className={classes.infoRow}>
        <span className={classes.infoLabel}>ID</span>
        <span className={classes.infoValue}>{el.id || '—'}</span>
      </div>

      {el.sortOrder !== undefined && (
        <div className={classes.infoRow}>
          <span className={classes.infoLabel}>Порядок сортировки</span>
          <span className={classes.infoValue}>{el.sortOrder}</span>
        </div>
      )}

      <div className={classes.infoRow}>
        <span className={classes.infoLabel}>Тип</span>
        <span className={classes.infoValue}>
          {el.systemType === SystemType.MODIFIERS ? '❌ Системная' : '✅ Обычная'}
        </span>
      </div>

      {el.isActive !== undefined && (
        <div className={classes.infoRow}>
          <span className={classes.infoLabel}>Активна</span>
          <span className={classes.infoValue}>{el.isActive ? '✅' : '❌'}</span>
        </div>
      )}

      {el.createdAt && (
        <div className={classes.infoRow}>
          <span className={classes.infoLabel}>Создана</span>
          <span className={classes.infoValue}>{formatDate(el.createdAt)}</span>
        </div>
      )}

      {el.updatedAt && (
        <div className={classes.infoRow}>
          <span className={classes.infoLabel}>Обновлена</span>
          <span className={classes.infoValue}>{formatDate(el.updatedAt)}</span>
        </div>
      )}

      {/* ============================================================ */}
      {/* ДОЧЕРНИЕ КАТЕГОРИИ */}
      {/* ============================================================ */}
      {el.children && el.children.length > 0 && (
        <>
          <div className={classes.sectionTitle}>Дочерние категории ({el.children.length})</div>
          <div className={classes.childrenList}>
            {(el.children as CategoryInterface[]).map((child) => (
              <div key={child.id} className={classes.childItem}>
                <span className={classes.childName}>{child.name}</span>
                <div className={classes.childBadges}>
                  {child.sortOrder !== undefined && (
                    <span className={classes.childBadge}>{child.sortOrder}</span>
                  )}
                  {child.isActive !== undefined && (
                    <span className={classes.childBadge}>{child.isActive ? '✅' : '❌'}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ============================================================ */}
      {/* АТРИБУТЫ */}
      {/* ============================================================ */}
      {el.attributes && el.attributes.length > 0 && (
        <>
          <div className={classes.sectionTitle}>Атрибуты ({el.attributes.length})</div>
          <div className={classes.attributesList}>
            {el.attributes.map((attr) => (
              <div key={attr.id} className={classes.attributeItem}>
                <div className={classes.attributeHeader}>
                  <span className={classes.attributeName}>{attr.name}</span>
                  <span className={classes.attributeSort}>сорт. {attr.sortOrder}</span>
                </div>

                <div className={classes.attributeDetails}>
                  <span className={classes.attributeBadge}>{fieldTypeToText(attr.fieldType)}</span>
                  <span className={classes.attributeBadge}>{valueTypeToText(attr.valueType)}</span>
                  {attr.unit !== Unit.NOT_SELECTED && unitTypeToShort(attr.unit) && (
                    <span className={classes.attributeBadge}>{unitTypeToShort(attr.unit)}</span>
                  )}
                  <span className={classes.attributeBadge}>
                    {attr.isRequired ? 'Обязательный' : 'Необязательный'}
                  </span>
                  <span className={classes.attributeBadge}>
                    {attr.isVisible ? 'Видимый' : 'Скрытый'}
                  </span>
                </div>

                {attr.options && attr.options.length > 0 && (
                  <div className={classes.optionsList}>
                    <span className={classes.infoLabel}>Опции:</span>
                    {attr.options.map((opt, idx) => (
                      <span key={idx} className={classes.optionItem}>
                        {opt}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {/* ============================================================ */}
      {/* ИЗОБРАЖЕНИЯ */}
      {/* ============================================================ */}
      {el.files && el.files.length > 0 && (
        <>
          <div className={classes.sectionTitle}>Изображения</div>
          <div className={classes.images}>
            {el.files.map((path) => (
              <img key={path} className={classes.image} src={`${imageUrl}${path}`} alt={el.name} />
            ))}
          </div>
        </>
      )}
    </div>
  )
}
